import {
  GENERIC_AUTH_FAILURE,
  type AddressInput,
  type AuthOutcome,
  type CustomerApiClient,
  type LocationRequest,
  type SearchRequest,
  type SignUpInput,
} from './api-client.ts';

export interface CustomerApiErrorShape {
  readonly code: string;
  readonly message: string;
  readonly requestId: string;
  readonly details?: unknown;
}

/** A traceable API failure. Screens may show its message without mistaking it for an empty result. */
export class CustomerApiError extends Error implements CustomerApiErrorShape {
  readonly code: string;
  readonly requestId: string;
  readonly details?: unknown;

  constructor(error: CustomerApiErrorShape) {
    super(error.message);
    this.name = 'CustomerApiError';
    this.code = error.code;
    this.requestId = error.requestId;
    this.details = error.details;
  }
}

export interface CustomerHttpClientOptions {
  /** Build-time API origin. An empty value intentionally means same origin. */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /** A bounded wait keeps customer screens from remaining in a loading state indefinitely. */
  readonly timeoutMs?: number;
}

type ErrorEnvelope = {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly details?: unknown;
  };
  readonly requestId?: unknown;
};

function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  return `bb_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function queryString(
  input: Readonly<Record<string, string | number | boolean | undefined>>,
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const serialized = query.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

function endpoint(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, '')}${path}`;
}

function requireErrorEnvelope(value: unknown, status: number): CustomerApiError {
  const body = value as ErrorEnvelope;
  const code = typeof body.error?.code === 'string' ? body.error.code : `HTTP_${status}`;
  const message =
    typeof body.error?.message === 'string'
      ? body.error.message
      : 'The requested service is unavailable.';
  const requestId =
    typeof body.requestId === 'string' ? body.requestId : 'request-id-not-returned-by-api';
  return new CustomerApiError({ code, message, requestId, details: body.error?.details });
}

/**
 * The screen contract was written ahead of several API routes. Do not invent a
 * plausible URL for those methods: callers receive a traceable, explicit
 * contract failure instead of a misleading empty result or an arbitrary 404.
 */
function unavailableContract(capability: string): CustomerApiError {
  return new CustomerApiError({
    code: 'API_CONTRACT_UNAVAILABLE',
    message: `${capability} is not available from the current customer API.`,
    requestId: 'customer-browser-contract',
  });
}

/**
 * Browser implementation of the screen contract. Screen modules keep receiving
 * this interface instead of importing fetch, which preserves their isolated DOM
 * and accessibility tests.
 */
export function createCustomerHttpClient(
  options: CustomerHttpClientOptions = {},
): CustomerApiClient {
  const baseUrl = options.baseUrl ?? import.meta.env.VITE_CUSTOMER_API_BASE_URL ?? '';
  const fetchImpl = options.fetchImpl ?? fetch;
  const timeoutMs = options.timeoutMs ?? 10_000;

  const request = async <T>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    idempotencyKey?: string,
  ): Promise<T> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') headers['Idempotency-Key'] = idempotencyKey ?? makeIdempotencyKey();

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
      response = await fetchImpl(endpoint(baseUrl, path), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        credentials: 'include',
        signal: controller.signal,
      });
    } catch (error) {
      if (controller.signal.aborted)
        throw new CustomerApiError({
          code: 'REQUEST_TIMEOUT',
          message: 'The request took too long. Please try again.',
          requestId: 'not-available',
        });
      throw error;
    } finally {
      clearTimeout(timeout);
    }
    const payload: unknown =
      response.status === 204 ? undefined : await response.json().catch(() => undefined);
    if (!response.ok) throw requireErrorEnvelope(payload, response.status);
    return payload as T;
  };

  const searchQuery = (input: SearchRequest, marketId?: string) => {
    const filters = Object.entries(input.filters ?? {}).flatMap(([key, values]) =>
      values.map((value) => [key, value] as const),
    );
    const query = new URLSearchParams();
    if (input.term !== undefined && input.term.trim() !== '') query.set('query', input.term);
    if (input.categorySlug !== undefined) query.set('category', input.categorySlug);
    if (input.sort !== undefined) query.set('sort', input.sort);
    if (input.page !== undefined) query.set('page', String(input.page));
    if (marketId !== undefined) query.set('marketId', marketId);
    for (const [key, value] of filters) query.append(key, value);
    const serialized = query.toString();
    return serialized === '' ? '/api/v1/customer/search' : `/api/v1/customer/search?${serialized}`;
  };

  const addressBody = (input: AddressInput) => ({
    label: input.label === '' ? null : input.label,
    recipient_name: input.recipientName,
    phone: input.phone === undefined || input.phone === '' ? null : input.phone,
    line1: input.line1,
    line2: input.line2 === '' || input.line2 === undefined ? null : input.line2,
    city: input.city,
    region: input.region,
    postal_code: input.postalCode,
    country_code:
      input.countryCode === undefined || input.countryCode === '' ? 'US' : input.countryCode,
    latitude: null,
    longitude: null,
    delivery_notes:
      input.deliveryNotes === '' || input.deliveryNotes === undefined ? null : input.deliveryNotes,
  });

  return {
    bootstrap: {
      async get(marketId) {
        return request('GET', `/api/v1/customer/bootstrap${queryString({ marketId })}`);
      },
    },
    discovery: {
      async homepage(marketId) {
        const payload = await request<{ sections: unknown }>(
          'GET',
          `/api/v1/customer/homepage${queryString({ marketId })}`,
        );
        return payload.sections as Awaited<ReturnType<CustomerApiClient['discovery']['homepage']>>;
      },
      async categories(marketId) {
        const payload = await request<{ categories: unknown }>(
          'GET',
          `/api/v1/customer/categories${queryString({ marketId })}`,
        );
        return payload.categories as Awaited<
          ReturnType<CustomerApiClient['discovery']['categories']>
        >;
      },
      async category(slug, marketId) {
        return request(
          'GET',
          `/api/v1/customer/categories/${encodeURIComponent(slug)}${queryString({ marketId })}`,
        );
      },
    },
    search: {
      async search(input, marketId) {
        return request('GET', searchQuery(input, marketId));
      },
      async suggestions(term, marketId) {
        return request(
          'GET',
          `/api/v1/customer/search/suggestions${queryString({ query: term, marketId })}`,
        );
      },
    },
    products: {
      async detail(slug, marketId) {
        return request(
          'GET',
          `/api/v1/customer/products/${encodeURIComponent(slug)}${queryString({ marketId })}`,
        );
      },
      async related(slug) {
        return request('GET', `/api/v1/customer/products/${encodeURIComponent(slug)}/related`);
      },
    },
    stores: {
      async nearby(marketId) {
        const payload = await request<{ stores: unknown }>(
          'GET',
          `/api/v1/customer/stores${queryString({ marketId })}`,
        );
        return payload.stores as Awaited<ReturnType<CustomerApiClient['stores']['nearby']>>;
      },
      async afterHours(marketId) {
        const payload = await request<{ stores: unknown }>(
          'GET',
          `/api/v1/customer/after-hours${queryString({ marketId })}`,
        );
        return payload.stores as Awaited<ReturnType<CustomerApiClient['stores']['afterHours']>>;
      },
      async detail(slug, marketId) {
        return request(
          'GET',
          `/api/v1/customer/stores/${encodeURIComponent(slug)}${queryString({ marketId })}`,
        );
      },
    },
    location: {
      async resolve(input: LocationRequest) {
        return request('POST', '/api/v1/customer/location/resolve', {
          postalCode: input.postalCode,
          city: input.city,
          region: input.region,
        });
      },
    },
    account: {
      async hub() {
        return request('GET', '/api/v1/customer/account');
      },
      async profile() {
        return request('GET', '/api/v1/customer/account/profile');
      },
      async consents() {
        return request('GET', '/api/v1/customer/account/consents');
      },
      async recordConsent(input) {
        return request('POST', '/api/v1/customer/account/consents', {
          ...input,
          source: 'ACCOUNT_SETTINGS',
        });
      },
    },
    favorites: {
      async page() {
        return request('GET', '/api/v1/customer/favorites');
      },
      async favoriteProduct(productId) {
        await request('POST', '/api/v1/customer/favorites/products', { productId });
      },
      async unfavoriteProduct(productId) {
        await request(
          'DELETE',
          `/api/v1/customer/favorites/products/${encodeURIComponent(productId)}`,
        );
      },
      async favoriteStore(input) {
        await request('POST', '/api/v1/customer/favorites/stores', input);
      },
      async unfavoriteStore(favoriteId) {
        await request(
          'DELETE',
          `/api/v1/customer/favorites/stores/${encodeURIComponent(favoriteId)}`,
        );
      },
    },
    lists: {
      async list() {
        return request('GET', '/api/v1/customer/lists');
      },
      async read(listId) {
        return request('GET', `/api/v1/customer/lists/${encodeURIComponent(listId)}`);
      },
      async create(name) {
        return request('POST', '/api/v1/customer/lists', { name });
      },
      async rename(listId, name) {
        await request('PATCH', `/api/v1/customer/lists/${encodeURIComponent(listId)}`, { name });
      },
      async delete(listId) {
        await request('DELETE', `/api/v1/customer/lists/${encodeURIComponent(listId)}`);
      },
      async addItem(listId, input) {
        return request('POST', `/api/v1/customer/lists/${encodeURIComponent(listId)}/items`, input);
      },
      async removeItem(listId, itemId) {
        await request(
          'DELETE',
          `/api/v1/customer/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(itemId)}`,
        );
      },
      async addAllAvailableToCart(listId) {
        void listId;
        throw unavailableContract('Add all available shopping-list items to cart');
      },
    },
    addresses: {
      async list() {
        return request('GET', '/api/v1/customer/addresses');
      },
      async create(input) {
        return request('POST', '/api/v1/customer/addresses', addressBody(input));
      },
      async update(addressId, input) {
        await request(
          'PATCH',
          `/api/v1/customer/addresses/${encodeURIComponent(addressId)}`,
          addressBody(input),
        );
      },
      async delete(addressId) {
        await request('DELETE', `/api/v1/customer/addresses/${encodeURIComponent(addressId)}`);
      },
      async setDefault(addressId) {
        await request(
          'POST',
          `/api/v1/customer/addresses/${encodeURIComponent(addressId)}/default`,
        );
      },
    },
    cart: {
      async read(addressId) {
        return request('GET', `/api/v1/customer/cart${queryString({ addressId })}`);
      },
      async addItem(input) {
        return request('POST', '/api/v1/customer/cart/items', input);
      },
      async changeQuantity(itemId, quantity) {
        return request('PATCH', `/api/v1/customer/cart/items/${encodeURIComponent(itemId)}`, {
          quantity,
        });
      },
      async removeItem(itemId) {
        return request('DELETE', `/api/v1/customer/cart/items/${encodeURIComponent(itemId)}`);
      },
      async changeFulfillmentMode(groupId, fulfillmentMode) {
        return request(
          'PATCH',
          `/api/v1/customer/cart/groups/${encodeURIComponent(groupId)}/fulfillment`,
          { fulfillmentMode },
        );
      },
    },
    checkout: {
      async createPaymentToken(input) {
        return request('POST', '/api/v1/customer/payment-tokens', {
          provider: input.provider,
        });
      },
      async place(input) {
        return request(
          'POST',
          '/api/v1/customer/orders',
          {
            cartId: input.cartId,
            addressId: input.addressId,
            deliveryQuoteId: input.deliveryQuoteId,
            paymentTokenReference: input.paymentTokenReference,
            provider: input.provider,
          },
          input.idempotencyKey,
        );
      },
      async capture(input) {
        return request(
          'POST',
          `/api/v1/customer/orders/${encodeURIComponent(input.orderId)}/payments/capture`,
          { amountMinor: input.amountMinor },
          input.idempotencyKey,
        );
      },
    },
    membership: {
      async plans() {
        const payload = await request<{ plans: unknown }>(
          'GET',
          '/api/v1/customer/membership/plans',
        );
        return payload.plans as Awaited<ReturnType<CustomerApiClient['membership']['plans']>>;
      },
      async enroll(input) {
        return request('POST', '/api/v1/customer/membership/enroll', input);
      },
    },
    notifications: {
      async get() {
        return request('GET', '/api/v1/customer/notification-preferences');
      },
      async update(input) {
        return request('PUT', '/api/v1/customer/notification-preferences', input);
      },
    },
    orders: {
      async list() {
        const payload = await request<{ orders: unknown }>('GET', '/api/v1/customer/orders');
        return payload.orders as Awaited<ReturnType<CustomerApiClient['orders']['list']>>;
      },
      async detail(orderId) {
        return request('GET', `/api/v1/customer/orders/${encodeURIComponent(orderId)}`);
      },
      async cancel(orderId, reason) {
        return request('POST', `/api/v1/customer/orders/${encodeURIComponent(orderId)}/cancel`, {
          ...(reason === undefined ? {} : { reason }),
        });
      },
      async tracking(fulfilmentId) {
        return request(
          'GET',
          `/api/v1/customer/delivery-tracking/${encodeURIComponent(fulfilmentId)}`,
        );
      },
      async requestReturn(subOrderId, input) {
        return request(
          'POST',
          `/api/v1/customer/sub-orders/${encodeURIComponent(subOrderId)}/returns`,
          input,
        );
      },
      async returnDetail(returnRequestId) {
        return request('GET', `/api/v1/customer/returns/${encodeURIComponent(returnRequestId)}`);
      },
      async cancelReturn(returnRequestId, reason) {
        return request(
          'POST',
          `/api/v1/customer/returns/${encodeURIComponent(returnRequestId)}/cancel`,
          reason === undefined ? {} : { reason },
        );
      },
    },
    leads: {
      async submitCustomer(input) {
        return request('POST', '/api/v1/customer/leads', input);
      },
      async submitRetailer(input) {
        return request('POST', '/api/v1/retailer/leads', input);
      },
      async submitMembershipInterest(input) {
        return request('POST', '/api/v1/membership/interests', input);
      },
    },
    auth: {
      async signIn(input): Promise<AuthOutcome> {
        try {
          await request('POST', '/api/v1/auth/session', input);
          const actor = await request<{ userId: string }>('GET', '/api/v1/auth/me');
          return { ok: true, customerId: actor.userId };
        } catch (error) {
          if (error instanceof CustomerApiError && error.code === 'UNAUTHENTICATED')
            return { ok: false, message: GENERIC_AUTH_FAILURE };
          throw error;
        }
      },
      async signUp(input: SignUpInput): Promise<AuthOutcome> {
        const payload = await request<{ userId?: string }>('POST', '/api/v1/auth/register', {
          email: input.email,
          password: input.password,
          fullName: input.displayName,
          marketingEmailConsent: input.marketingEmailConsent,
          marketingSmsConsent: input.marketingSmsConsent,
          ...(input.phone === undefined ? {} : { phone: input.phone }),
          ...(input.postalCode === undefined ? {} : { postalCode: input.postalCode }),
          ...(input.signupSource === undefined ? {} : { signupSource: input.signupSource }),
          ...(input.visitorId === undefined ? {} : { visitorId: input.visitorId }),
        });
        return payload.userId === undefined
          ? { ok: false, message: GENERIC_AUTH_FAILURE }
          : { ok: true, customerId: payload.userId };
      },
      async requestPasswordReset(email) {
        void email;
        throw unavailableContract('Password reset');
      },
      async signOut() {
        await request('DELETE', '/api/v1/auth/session');
      },
    },
  };
}
